import { fmtDate } from "@/lib/dateFormat";
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Banknote, TrendingUp, Clock, Receipt, FileText, AlertTriangle, Download, FileSpreadsheet, Loader2, Plus, Trash2, Pencil, Paperclip, Search, X, ExternalLink, Car, User, Building2, Wrench, Lock, Package } from "lucide-react";
import { api, money, downloadPdf, uploadImage, API_BASE, createSupplierWithDupCheck } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Loading } from "@/components/States";
import { StatusChip } from "@/components/StatusChip";
import { PartPicker } from "@/components/PartPicker";
import { useAuth } from "@/lib/auth";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ReportsInsights } from "@/components/ReportsInsights";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

const PERIODS = [["week", "7 days"], ["month", "30 days"], ["year", "Year"], ["all", "All time"]];
const EXPENSE_CATEGORIES = ["Parts", "Labour", "Vehicle repair", "Fuel", "Tools", "Equipment", "Rent", "Utilities", "Insurance", "Subscriptions", "Office supplies", "Subcontractor", "Miscellaneous"];
const PAYMENT_METHODS = [["cash", "Cash"], ["bank_transfer", "Bank Transfer"], ["card", "Business Card"], ["direct_debit", "Direct Debit"], ["other", "Other"]];
const PAYMENT_STATUSES = [["paid", "Paid"], ["unpaid", "Unpaid"], ["partial", "Partially paid"]];
const absUrl = (u) => (u && u.startsWith("/api") ? `${API_BASE.replace(/\/api$/, "")}${u}` : u);

export default function Money() {
  const { user } = useAuth();
  const isOwner = user?.role === "owner";
  const [period, setPeriod] = useState("month");
  const [fin, setFin] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [overdue, setOverdue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get("tab") || "invoices";
  const setTab = (v) => setSearchParams(v === "invoices" ? {} : { tab: v }, { replace: true });
  const [invFilter, setInvFilter] = useState("");
  const [addExpense, setAddExpense] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [f, i, o] = await Promise.all([api(`/reports/financial?period=${period}`), api("/invoices"), api("/reports/overdue")]);
      setFin(f); setInvoices(i); setOverdue(o);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [period]);

  const delInvoice = async (inv) => {
    const warn = Number(inv.total_paid) > 0
      ? `\n\n⚠ This invoice has recorded payments (${money(inv.total_paid)}). The payment records will be kept for your accounts.`
      : "";
    if (!window.confirm(`Archive invoice ${inv.invoice_number}?\n\nIt will be removed from your lists but the record and audit trail are preserved. The invoice number is never reused.${warn}`)) return;
    try { await api(`/invoices/${inv.id}`, { method: "DELETE" }); toast.success(`Invoice ${inv.invoice_number} archived`); load(); } catch (e) { toast.error(e.message); }
  };

  if (loading && !fin) return <Loading />;

  return (
    <div>
      <PageHeader title="Finance" subtitle="Revenue, invoices and overdue balances"
        actions={<div className="flex gap-2">
          <button onClick={() => { setTab("expenses"); setAddExpense(true); }} data-testid="finance-add-expense-btn" className="inline-flex items-center gap-2 rounded-lg border border-brand px-4 py-2.5 text-sm font-bold text-brand transition-transform hover:scale-105"><Plus className="h-4 w-4" />Add Expense</button>
          <Link to="/invoice/new" data-testid="new-invoice-btn" className="inline-flex items-center gap-2 rounded-lg bg-brand px-4 py-2.5 text-sm font-bold text-white transition-transform hover:scale-105"><Plus className="h-4 w-4" />New Invoice</Link>
        </div>} />

      <div className="mb-8">
        <div className="mb-4 flex items-center gap-2">
          <TrendingUp className="h-5 w-5 text-brand" />
          <h2 className="font-display text-xl font-bold text-midnight">Reports &amp; Insights</h2>
        </div>
        <ReportsInsights />
      </div>

      <div className="mb-4 flex flex-wrap gap-2">
        {PERIODS.map(([k, l]) => (
          <button key={k} onClick={() => setPeriod(k)} data-testid={`period-${k}`} className={`rounded-full px-4 py-1.5 text-xs font-bold uppercase tracking-wide ${period === k ? "bg-midnight text-white" : "bg-secondary text-slate-500"}`}>{l}</button>
        ))}
      </div>

      {fin && (
        <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
          <Stat icon={Banknote} label="Revenue" value={money(fin.revenue)} tone="success" onClick={() => { setInvFilter(""); setTab("invoices"); }} testid="stat-revenue" />
          <Stat icon={Clock} label="Outstanding" value={money(fin.outstanding)} tone="warning" onClick={() => setTab("overdue")} testid="stat-outstanding" />
          <Stat icon={Receipt} label="Expenses" value={money(fin.expenses)} tone="midnight" onClick={() => setTab("expenses")} testid="stat-expenses" />
          <Stat icon={TrendingUp} label="Net Profit" value={money(fin.net_profit)} tone="brand" onClick={() => setTab("expenses")} testid="stat-net-profit" />
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-3">
          <TabsTrigger value="invoices" data-testid="tab-money-invoices"><FileText className="mr-1.5 h-4 w-4" />Invoices ({invoices.length})</TabsTrigger>
          <TabsTrigger value="overdue" data-testid="tab-money-overdue"><AlertTriangle className="mr-1.5 h-4 w-4" />Overdue ({overdue.length})</TabsTrigger>
          <TabsTrigger value="expenses" data-testid="tab-money-expenses"><Receipt className="mr-1.5 h-4 w-4" />Expenses</TabsTrigger>
        </TabsList>
        <TabsContent value="invoices">
          {invFilter && (
            <div className="mb-3 flex items-center gap-2 text-xs" data-testid="inv-filter-bar">
              <span className="rounded-full bg-brand-tertiary px-3 py-1 font-bold text-brand">Showing: {invFilter}</span>
              <button onClick={() => setInvFilter("")} data-testid="inv-filter-clear" className="font-semibold text-brand-2 underline">Clear</button>
            </div>
          )}
          {(() => {
            const shown = invFilter ? invoices.filter((i) => (i.status === "void" ? "unpaid" : i.payment_status || "unpaid") === invFilter) : invoices;
            return shown.length === 0 ? <p className="rounded-lg border border-dashed border-border p-6 text-center text-sm text-muted-foreground">No invoices{invFilter ? ` marked ${invFilter}` : " yet"}.</p> : (
            <div className="divide-y divide-border overflow-hidden rounded-xl border border-border bg-card">
              {shown.map((inv) => (
                <div key={inv.id} className="flex items-center gap-3 px-4 py-3.5 hover:bg-secondary">
                  <Link to={`/invoice/${inv.id}`} data-testid={`money-inv-${inv.id}`} className="flex min-w-0 flex-1 items-center gap-4">
                    <div className="min-w-0 flex-1"><p className="truncate text-sm font-bold text-midnight">{inv.invoice_number}</p><p className="truncate text-xs text-muted-foreground">{inv.title} · {fmtDate(inv.created_at)}</p></div>
                    <p className="font-display text-lg font-bold text-midnight">{money(inv.total_gross)}</p>
                    <StatusChip status={inv.status === "void" ? "unpaid" : inv.payment_status || "unpaid"} />
                  </Link>
                  {isOwner && <button onClick={() => delInvoice(inv)} data-testid={`money-inv-del-${inv.id}`} title="Delete invoice" className="shrink-0 text-danger hover:text-danger/80"><Trash2 className="h-4 w-4" /></button>}
                </div>
              ))}
            </div>
          ); })()}
        </TabsContent>
        <TabsContent value="overdue">
          {overdue.length === 0 ? <p className="rounded-lg border border-dashed border-border p-6 text-center text-sm text-muted-foreground">Nothing overdue. Nice work!</p> : (
            <div className="divide-y divide-border overflow-hidden rounded-xl border border-border bg-card">
              {overdue.map((inv) => (
                <Link key={inv.id} to={`/invoice/${inv.id}`} className="flex items-center gap-4 px-4 py-3.5 hover:bg-secondary">
                  <div className="min-w-0 flex-1"><p className="truncate text-sm font-bold text-midnight">{inv.invoice_number} · {inv.customer?.name || "—"}</p><p className="text-xs text-danger">{inv.days_overdue} days overdue</p></div>
                  <p className="font-display text-lg font-bold text-warning">{money(inv.balance)}</p>
                </Link>
              ))}
            </div>
          )}
        </TabsContent>
        <TabsContent value="expenses">
          <ExpensesPanel isOwner={isOwner} onChanged={load} autoOpen={addExpense} onAutoOpenDone={() => setAddExpense(false)} />
        </TabsContent>
      </Tabs>

      <div className="mt-8 border-t border-border pt-6">
        <AccountingExport />
      </div>
    </div>
  );
}

function ExpensesPanel({ isOwner, onChanged, autoOpen, onAutoOpenDone }) {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expOpen, setExpOpen] = useState(false);
  const [editExp, setEditExp] = useState(null);
  const [viewExp, setViewExp] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [f, setF] = useState({ q: "", category: "", payment_status: "", start: "", end: "", sort: "date", order: "desc" });

  useEffect(() => { if (autoOpen) { setExpOpen(true); onAutoOpenDone?.(); } }, [autoOpen, onAutoOpenDone]);

  const thisMonthRange = () => {
    const d = new Date();
    const start = new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
    const end = new Date(d.getFullYear(), d.getMonth() + 1, 0).toISOString().slice(0, 10);
    return { start, end };
  };

  const qs = () => {
    const p = new URLSearchParams();
    Object.entries(f).forEach(([k, v]) => { if (v) p.set(k, v); });
    return p.toString();
  };

  const load = async () => {
    setLoading(true);
    try {
      const [list, sum] = await Promise.all([api(`/expenses?${qs()}`), api("/expenses/summary")]);
      setRows(list); setSummary(sum);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [f]);

  const del = async (id) => {
    if (!window.confirm("Delete this expense? This cannot be undone.")) return;
    try { await api(`/expenses/${id}`, { method: "DELETE" }); toast.success("Expense deleted"); load(); onChanged?.(); } catch (e) { toast.error(e.message); }
  };

  const doExport = async () => {
    setExporting(true);
    try { await downloadPdf(`/expenses/export?${qs()}`, "expenses.csv"); toast.success("CSV downloaded"); }
    catch (e) { toast.error(e.message || "Export failed"); } finally { setExporting(false); }
  };

  const toggleSort = (col) => setF((x) => ({ ...x, sort: col, order: x.sort === col && x.order === "desc" ? "asc" : "desc" }));

  return (
    <div>
      {summary && (
        <div className="mb-5 grid grid-cols-2 gap-3 lg:grid-cols-4" data-testid="expense-summary">
          <MiniStat label="Total expenses" value={money(summary.total)} testid="exp-sum-total" onClick={() => setF((x) => ({ ...x, payment_status: "", start: "", end: "" }))} />
          <MiniStat label="Paid" value={money(summary.paid)} tone="success" testid="exp-sum-paid" onClick={() => setF((x) => ({ ...x, payment_status: "paid" }))} />
          <MiniStat label="Unpaid" value={money(summary.unpaid)} tone="warning" testid="exp-sum-unpaid" onClick={() => setF((x) => ({ ...x, payment_status: "unpaid" }))} />
          <MiniStat label="This month" value={money(summary.this_month)} tone="brand" testid="exp-sum-month" onClick={() => setF((x) => ({ ...x, ...thisMonthRange() }))} />
        </div>
      )}
      {summary && (summary.by_category.length > 0 || summary.supplier_balances.length > 0) && (
        <div className="mb-5 grid gap-3 md:grid-cols-2">
          <div className="rounded-xl border border-border bg-card p-4" data-testid="exp-by-category">
            <p className="mb-2 text-[11px] font-bold uppercase tracking-widest text-muted-foreground">Expenses by category</p>
            {summary.by_category.length === 0 ? <p className="text-sm text-muted-foreground">None yet.</p> : summary.by_category.map((c) => (
              <div key={c.category} className="flex justify-between py-1 text-sm"><span className="text-midnight">{c.category}</span><span className="font-semibold">{money(c.total)}</span></div>
            ))}
          </div>
          <div className="rounded-xl border border-border bg-card p-4" data-testid="exp-supplier-balances">
            <p className="mb-2 text-[11px] font-bold uppercase tracking-widest text-muted-foreground">Supplier balances outstanding</p>
            {summary.supplier_balances.length === 0 ? <p className="text-sm text-muted-foreground">All suppliers settled.</p> : summary.supplier_balances.map((s) => (
              <div key={s.supplier} className="flex justify-between py-1 text-sm"><span className="text-midnight">{s.supplier}</span><span className="font-semibold text-warning">{money(s.outstanding)}</span></div>
            ))}
          </div>
        </div>
      )}

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-2 rounded-lg border border-input bg-card px-3 py-2">
          <Search className="h-4 w-4 text-muted-foreground" />
          <input value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} data-testid="exp-search" placeholder="Search supplier, customer, reg, description…" className="w-56 bg-transparent text-sm outline-none" />
          {f.q && <button onClick={() => setF({ ...f, q: "" })}><X className="h-3.5 w-3.5 text-muted-foreground" /></button>}
        </div>
        <select value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} data-testid="exp-filter-category" className="rounded-lg border border-input bg-card px-3 py-2 text-sm">
          <option value="">All categories</option>{EXPENSE_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={f.payment_status} onChange={(e) => setF({ ...f, payment_status: e.target.value })} data-testid="exp-filter-status" className="rounded-lg border border-input bg-card px-3 py-2 text-sm">
          <option value="">Any status</option>{PAYMENT_STATUSES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <input type="date" value={f.start} onChange={(e) => setF({ ...f, start: e.target.value })} data-testid="exp-filter-start" className="rounded-lg border border-input bg-card px-3 py-2 text-sm" />
        <input type="date" value={f.end} onChange={(e) => setF({ ...f, end: e.target.value })} data-testid="exp-filter-end" className="rounded-lg border border-input bg-card px-3 py-2 text-sm" />
        <div className="ml-auto flex gap-2">
          <button onClick={doExport} disabled={exporting} data-testid="exp-export-csv" className="inline-flex items-center gap-2 rounded-lg border border-brand px-3 py-2 text-sm font-bold text-brand disabled:opacity-60">{exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4" />}CSV</button>
          <button onClick={() => { setEditExp(null); setExpOpen(true); }} data-testid="add-expense-btn" className="inline-flex items-center gap-2 rounded-lg bg-brand px-4 py-2 text-sm font-bold text-white"><Plus className="h-4 w-4" />Add Expense</button>
        </div>
      </div>

      {loading ? <Loading /> : rows.length === 0 ? <p className="rounded-lg border border-dashed border-border p-6 text-center text-sm text-muted-foreground">No expenses match your filters.</p> : (
        <div className="overflow-x-auto rounded-xl border border-border bg-card">
          <table className="w-full min-w-[920px] text-sm">
            <thead className="bg-secondary text-[10px] uppercase tracking-widest text-muted-foreground">
              <tr>
                <Th onClick={() => toggleSort("date")} active={f.sort === "date"} order={f.order}>Date</Th>
                <th className="px-3 py-2.5 text-left">Description</th>
                <th className="px-3 py-2.5 text-left">Category</th>
                <th className="px-3 py-2.5 text-left">Supplier</th>
                <th className="px-3 py-2.5 text-left">Customer</th>
                <th className="px-3 py-2.5 text-left">Vehicle</th>
                <th className="px-3 py-2.5 text-left">Job</th>
                <Th onClick={() => toggleSort("amount")} active={f.sort === "amount"} order={f.order} className="text-right">Total</Th>
                <th className="px-3 py-2.5 text-left">Status</th>
                <th className="px-3 py-2.5 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {rows.map((e) => (
                <tr key={e.id} data-testid={`expense-${e.id}`} className="hover:bg-secondary">
                  <td className="px-3 py-2.5 whitespace-nowrap">{fmtDate(e.date)}</td>
                  <td className="px-3 py-2.5 max-w-[180px] truncate">{e.description || "—"}{e.purchase_id && <span data-testid={`expense-linked-${e.id}`} className="ml-2 rounded-full bg-brand-tertiary px-1.5 py-0.5 text-[9px] font-bold uppercase text-brand-2">Purchase</span>}</td>
                  <td className="px-3 py-2.5">{e.category}</td>
                  <td className="px-3 py-2.5">{e.supplier_name || "—"}</td>
                  <td className="px-3 py-2.5">{e.customer_name || "—"}</td>
                  <td className="px-3 py-2.5 font-mono uppercase">{e.vehicle_vrm || "—"}</td>
                  <td className="px-3 py-2.5 max-w-[120px] truncate">{e.job_title || "—"}</td>
                  <td className="px-3 py-2.5 text-right font-bold">{money(e.total)}</td>
                  <td className="px-3 py-2.5"><StatusChip status={e.payment_status === "partial" ? "part_paid" : e.payment_status || "paid"} /></td>
                  <td className="px-3 py-2.5">
                    <div className="flex items-center justify-end gap-2">
                      <button onClick={() => setViewExp(e)} data-testid={`expense-view-${e.id}`} title="View" className="text-brand-2"><ExternalLink className="h-4 w-4" /></button>
                      {e.purchase_id ? (
                        <span title="Linked to a supplier purchase — edit the purchase" data-testid={`expense-locked-${e.id}`} className="text-muted-foreground"><Lock className="h-4 w-4" /></span>
                      ) : (
                        <>
                          <button onClick={() => { setEditExp(e); setExpOpen(true); }} data-testid={`expense-edit-${e.id}`} title="Edit" className="text-brand-2"><Pencil className="h-4 w-4" /></button>
                          {isOwner && <button onClick={() => del(e.id)} data-testid={`expense-del-${e.id}`} title="Delete" className="text-danger"><Trash2 className="h-4 w-4" /></button>}
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ExpenseModal open={expOpen} onClose={() => setExpOpen(false)} expense={editExp} onSaved={() => { setExpOpen(false); load(); onChanged?.(); }} />
      <ExpenseView expense={viewExp} onClose={() => setViewExp(null)} onEdit={(e) => { setViewExp(null); setEditExp(e); setExpOpen(true); }} />
    </div>
  );
}

function Th({ children, onClick, active, order, className = "" }) {
  return <th onClick={onClick} className={`cursor-pointer select-none px-3 py-2.5 text-left hover:text-midnight ${className}`}>{children}{active ? (order === "desc" ? " ↓" : " ↑") : ""}</th>;
}

function MiniStat({ label, value, tone = "midnight", testid, onClick }) {
  const tones = { brand: "text-brand", success: "text-success", warning: "text-warning", midnight: "text-midnight" };
  const inner = (<>
    <p className={`font-display text-xl font-bold ${tones[tone]}`}>{value}</p>
    <p className="mt-0.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{label}</p>
  </>);
  if (onClick) return <button onClick={onClick} data-testid={testid} className="rounded-xl border border-border bg-card p-4 text-left transition-colors hover:border-brand hover:bg-secondary">{inner}</button>;
  return <div className="rounded-xl border border-border bg-card p-4" data-testid={testid}>{inner}</div>;
}

function OpenLink({ to, icon: Icon, children, testid }) {
  return <Link to={to} data-testid={testid} className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-card px-3 py-2 text-sm font-semibold text-brand-2 hover:bg-secondary"><Icon className="h-4 w-4" />{children}<ExternalLink className="h-3 w-3" /></Link>;
}

function ExpenseView({ expense, onClose, onEdit }) {
  if (!expense) return null;
  const e = expense;
  return (
    <Dialog open={!!expense} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="expense-view-modal">
        <DialogHeader><DialogTitle>{e.category} · {money(e.total)}</DialogTitle></DialogHeader>
        <div className="space-y-2 text-sm">
          <Row k="Date" v={fmtDate(e.date)} />
          <Row k="Description" v={e.description || "—"} />
          <Row k="Net / VAT / Total" v={`${money(e.amount)} · ${money(e.vat || 0)} · ${money(e.total)}`} />
          <Row k="Payment" v={`${(PAYMENT_METHODS.find((m) => m[0] === e.payment_method) || [])[1] || e.payment_method || "—"} · ${e.payment_status || "paid"}${e.payment_status === "partial" ? ` (paid ${money(e.paid_amount || 0)}, outstanding ${money(e.outstanding || 0)})` : ""}`} />
          {e.notes && <Row k="Notes" v={e.notes} />}
          {e.receipt_url && <a href={absUrl(e.receipt_url)} target="_blank" rel="noreferrer" className="inline-block"><img src={absUrl(e.receipt_url)} alt="receipt" className="h-24 rounded border border-border object-cover" /></a>}
          <div className="flex flex-wrap gap-2 border-t border-border pt-3">
            {e.customer_id && <OpenLink to={`/customers/${e.customer_id}`} icon={User} testid="exp-open-customer">{e.customer_name || "Customer"}</OpenLink>}
            {e.vehicle_id && <OpenLink to={`/vehicles/${e.vehicle_id}`} icon={Car} testid="exp-open-vehicle">{e.vehicle_vrm || "Vehicle"}</OpenLink>}
            {e.supplier_id && <OpenLink to={`/suppliers`} icon={Building2} testid="exp-open-supplier">{e.supplier_name || "Supplier"}</OpenLink>}
            {e.job_id && <OpenLink to={`/jobs/${e.job_id}`} icon={Wrench} testid="exp-open-job">{e.job_title || "Job"}</OpenLink>}
          </div>
          <div className="flex justify-end pt-2">{e.purchase_id ? <span data-testid="exp-view-linked" className="inline-flex items-center gap-2 rounded-lg border border-border px-4 py-2 text-sm font-semibold text-muted-foreground"><Lock className="h-4 w-4" />Linked to a supplier purchase</span> : <button onClick={() => onEdit(e)} data-testid="exp-view-edit" className="inline-flex items-center gap-2 rounded-lg bg-brand px-4 py-2 text-sm font-bold text-white"><Pencil className="h-4 w-4" />Edit</button>}</div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Row({ k, v }) {
  return <div className="flex justify-between gap-4"><span className="text-muted-foreground">{k}</span><span className="text-right font-medium text-midnight">{v}</span></div>;
}

function ExpenseModal({ open, onClose, expense, onSaved }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [suppliers, setSuppliers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [newSupplier, setNewSupplier] = useState("");
  // Parts-category purchase details (revealed only when category === "Parts").
  const [pp, setPp] = useState({ part: null, newPartName: "", qty: 1, unit_cost: "", delivery: "received", qty_received: "", destination: "stock" });
  const [pickerOpen, setPickerOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    setNewSupplier("");
    setPp({ part: null, newPartName: "", qty: 1, unit_cost: "", delivery: "received", qty_received: "", destination: "stock" });
    Promise.all([api("/suppliers"), api("/customers"), api("/vehicles"), api("/jobs")])
      .then(([s, c, v, j]) => { setSuppliers(s || []); setCustomers(c || []); setVehicles(v || []); setJobs(j || []); })
      .catch(() => {});
    setForm(expense ? {
      category: expense.category || "Parts", supplier_id: expense.supplier_id || "", description: expense.description || "",
      date: (expense.date || "").slice(0, 10) || new Date().toISOString().slice(0, 10),
      amount: expense.amount ?? 0, vat: expense.vat ?? 0, payment_method: expense.payment_method || "card",
      payment_status: expense.payment_status || "paid", paid_amount: expense.paid_amount ?? 0,
      customer_id: expense.customer_id || "", vehicle_id: expense.vehicle_id || "", job_id: expense.job_id || "",
      notes: expense.notes || "", receipt_url: expense.receipt_url || "", receipt_media_id: expense.receipt_media_id || "",
    } : {
      category: "Parts", supplier_id: "", description: "", date: new Date().toISOString().slice(0, 10),
      amount: "", vat: "", payment_method: "card", payment_status: "paid", paid_amount: "",
      customer_id: "", vehicle_id: "", job_id: "", notes: "", receipt_url: "", receipt_media_id: "",
    });
  }, [open, expense]);

  const isParts = form.category === "Parts";
  const partsNet = (Number(pp.qty) || 0) * (Number(pp.unit_cost) || 0);
  const total = (isParts && (pp.part || pp.newPartName.trim()) ? partsNet : (Number(form.amount) || 0)) + (Number(form.vat) || 0);

  const onFile = async (ev) => {
    const file = ev.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const r = await uploadImage(file, { context: "receipt" });
      setForm((f) => ({ ...f, receipt_url: r.url, receipt_media_id: r.id }));
      toast.success("Receipt attached");
    } catch (e) { toast.error(e.message || "Upload failed"); } finally { setUploading(false); }
  };

  const save = async () => {
    if (!form.date) { toast.error("Pick a date"); return; }
    if (form.payment_status === "partial" && !(Number(form.paid_amount) >= 0)) { toast.error("Enter the amount paid"); return; }
    setSaving(true);
    try {
      let supplierId = form.supplier_id || null;
      let supplierName = suppliers.find((s) => s.id === supplierId)?.name || null;
      if (newSupplier.trim()) {
        const created = await createSupplierWithDupCheck(newSupplier.trim());
        if (!created) { setSaving(false); return; }
        supplierId = created.id; supplierName = created.name;
      }

      // Parts purchase path: a Parts expense with a supplier + part becomes a
      // supplier purchase, which creates exactly ONE linked money-out expense
      // and drives stock from the received quantity (no double counts).
      const hasPart = !!pp.part || !!pp.newPartName.trim();
      if (!expense && isParts && hasPart && supplierId) {
        if (pp.destination === "job" && !form.job_id) { toast.error("Choose the related job for a direct-to-job purchase"); setSaving(false); return; }
        if (!(Number(pp.qty) > 0)) { toast.error("Enter the quantity purchased"); setSaving(false); return; }
        let partId = pp.part?.id || null;
        let partName = pp.part?.name || pp.newPartName.trim();
        let createdPartId = null;
        if (!partId) {
          const createdPart = await api("/parts", { method: "POST", body: JSON.stringify({ name: partName, cost_price: Number(pp.unit_cost) || 0, unit_price: 0, stock_qty: 0 }) });
          partId = createdPart.id; partName = createdPart.name; createdPartId = createdPart.id;
        }
        const qty = Number(pp.qty) || 0;
        const status = pp.delivery === "not_received" ? "ordered" : pp.delivery === "partial" ? "partial" : "received";
        const received = pp.delivery === "not_received" ? 0 : pp.delivery === "received" ? qty : (Number(pp.qty_received) || 0);
        const toJob = pp.destination === "job";
        const line = {
          description: partName, qty, unit_cost: Number(pp.unit_cost) || 0, part_id: partId,
          allocation: toJob ? "job" : "stock", received_qty: received,
          job_id: toJob ? (form.job_id || null) : null,
          customer_id: toJob ? (form.customer_id || null) : null,
          vehicle_id: toJob ? (form.vehicle_id || null) : null,
        };
        const purchase = {
          supplier_id: supplierId,
          date: form.date ? new Date(form.date + "T12:00:00").toISOString() : null,
          vat: Number(form.vat) || 0, status, payment_status: form.payment_status || "unpaid",
          paid_amount: form.payment_status === "partial" ? (Number(form.paid_amount) || 0) : null,
          notes: form.notes || null, receipt_media_id: form.receipt_media_id || null,
          lines: [line],
        };
        try {
          await api("/purchases", { method: "POST", body: JSON.stringify(purchase) });
        } catch (err) {
          // Compensate: drop the just-created orphan part so nothing dangles.
          if (createdPartId) { try { await api(`/parts/${createdPartId}`, { method: "DELETE" }); } catch { /* best effort */ } }
          throw err;
        }
        toast.success("Parts purchase recorded — linked expense created");
        onSaved();
        return;
      }

      if (!(Number(form.amount) >= 0)) { toast.error("Enter a valid amount"); setSaving(false); return; }
      const body = {
        category: form.category, supplier_id: supplierId, supplier: supplierName,
        customer_id: form.customer_id || null, vehicle_id: form.vehicle_id || null, job_id: form.job_id || null,
        description: form.description || "", date: form.date ? new Date(form.date + "T12:00:00").toISOString() : null,
        amount: Number(form.amount) || 0, vat: Number(form.vat) || 0, payment_method: form.payment_method || null,
        payment_status: form.payment_status || "paid",
        paid_amount: form.payment_status === "partial" ? (Number(form.paid_amount) || 0) : null,
        notes: form.notes || null, receipt_media_id: form.receipt_media_id || null,
      };
      if (expense) await api(`/expenses/${expense.id}`, { method: "PATCH", body: JSON.stringify(body) });
      else await api("/expenses", { method: "POST", body: JSON.stringify(body) });
      toast.success(expense ? "Expense updated" : "Expense added"); onSaved();
    } catch (e) { toast.error(e.message || "Could not save expense"); } finally { setSaving(false); }
  };

  return (
    <>
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto" data-testid="expense-modal">
        <DialogHeader><DialogTitle>{expense ? "Edit expense" : "Add expense"}</DialogTitle></DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          <EF label="DATE"><input type="date" value={form.date || ""} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="exp-date" className={fieldCls} /></EF>
          <EF label="CATEGORY"><select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="exp-category" className={fieldCls}>{EXPENSE_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}</select></EF>

          <EF label="SUPPLIER"><select value={form.supplier_id || ""} onChange={(e) => { setForm({ ...form, supplier_id: e.target.value }); setNewSupplier(""); }} data-testid="exp-supplier" className={fieldCls}><option value="">— None / add new below —</option>{suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</select></EF>
          <EF label="OR ADD NEW SUPPLIER"><input value={newSupplier} onChange={(e) => setNewSupplier(e.target.value)} data-testid="exp-supplier-new" placeholder="New supplier name" className={fieldCls} /></EF>

          <EF label="CUSTOMER (optional)"><select value={form.customer_id || ""} onChange={(e) => setForm({ ...form, customer_id: e.target.value })} data-testid="exp-customer" className={fieldCls}><option value="">— None —</option>{customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select></EF>
          <EF label="VEHICLE (optional)"><select value={form.vehicle_id || ""} onChange={(e) => setForm({ ...form, vehicle_id: e.target.value })} data-testid="exp-vehicle" className={fieldCls}><option value="">— None —</option>{vehicles.map((v) => <option key={v.id} value={v.id}>{(v.vrm || "").toUpperCase()} · {v.make || ""} {v.model || ""}</option>)}</select></EF>

          <div className="sm:col-span-2"><EF label="RELATED JOB (optional)"><select value={form.job_id || ""} onChange={(e) => setForm({ ...form, job_id: e.target.value })} data-testid="exp-job" className={fieldCls}><option value="">— None —</option>{jobs.map((j) => <option key={j.id} value={j.id}>{j.title || "Job"}{j.invoice_number ? ` · ${j.invoice_number}` : ""}</option>)}</select></EF></div>

          <div className="sm:col-span-2"><EF label="DESCRIPTION"><input value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="exp-description" placeholder="Brake pads, oil filter…" className={fieldCls} /></EF></div>

          {isParts && (
            <div className="sm:col-span-2 rounded-lg border border-brand/30 bg-brand-tertiary/40 p-3" data-testid="exp-parts-block">
              <p className="mb-2 text-[11px] font-bold uppercase tracking-widest text-brand-2">Parts purchase (links to inventory & supplier)</p>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <EF label="PART">
                    <div className="flex flex-wrap items-center gap-2">
                      <button type="button" onClick={() => setPickerOpen(true)} data-testid="exp-pick-part" className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-card px-3 py-2 text-sm font-semibold text-brand-2 hover:bg-secondary">
                        <Package className="h-4 w-4" />{pp.part ? pp.part.name : "Choose from stock"}
                      </button>
                      {pp.part && <button type="button" onClick={() => setPp({ ...pp, part: null })} data-testid="exp-clear-part" className="text-xs font-semibold text-danger">Clear</button>}
                      <span className="text-xs text-muted-foreground">or</span>
                      <input value={pp.newPartName} onChange={(e) => setPp({ ...pp, newPartName: e.target.value, part: null })} data-testid="exp-new-part" placeholder="New part name" className="min-w-[160px] flex-1 rounded-lg border border-input px-3 py-2 text-sm" />
                    </div>
                  </EF>
                </div>
                <EF label="QUANTITY PURCHASED"><input type="number" step="1" min="0" value={pp.qty} onChange={(e) => setPp({ ...pp, qty: e.target.value })} data-testid="exp-part-qty" className={fieldCls} /></EF>
                <EF label="UNIT PURCHASE COST £"><input type="number" step="0.01" min="0" value={pp.unit_cost} onChange={(e) => setPp({ ...pp, unit_cost: e.target.value })} data-testid="exp-part-unit-cost" className={fieldCls} /></EF>
                <EF label="DELIVERY STATUS"><select value={pp.delivery} onChange={(e) => setPp({ ...pp, delivery: e.target.value })} data-testid="exp-part-delivery" className={fieldCls}><option value="not_received">Not received</option><option value="partial">Partially received</option><option value="received">Received</option></select></EF>
                {pp.delivery === "partial" && <EF label="QUANTITY RECEIVED"><input type="number" step="1" min="0" value={pp.qty_received} onChange={(e) => setPp({ ...pp, qty_received: e.target.value })} data-testid="exp-part-qty-received" className={fieldCls} /></EF>}
                <div className="sm:col-span-2"><EF label="PURCHASE DESTINATION"><select value={pp.destination} onChange={(e) => setPp({ ...pp, destination: e.target.value })} data-testid="exp-part-destination" className={fieldCls}><option value="stock">General Stock</option><option value="job">Direct to Job (uses Related Job above)</option></select></EF></div>
              </div>
              {(pp.part || pp.newPartName.trim()) && <p className="mt-2 text-xs text-muted-foreground" data-testid="exp-part-hint">Net {money(partsNet)} — a supplier purchase + one linked expense will be created. Stock increases only by the received quantity{pp.destination === "job" ? " (direct-to-job does not touch general stock)" : ""}.</p>}
            </div>
          )}

          {!(isParts && (pp.part || pp.newPartName.trim())) && (
            <EF label="AMOUNT (NET) £"><input type="number" step="0.01" min="0" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="exp-amount" className={fieldCls} /></EF>
          )}
          <EF label="VAT £ (optional)"><input type="number" step="0.01" min="0" value={form.vat} onChange={(e) => setForm({ ...form, vat: e.target.value })} data-testid="exp-vat" className={fieldCls} /></EF>

          <EF label="PAYMENT METHOD"><select value={form.payment_method} onChange={(e) => setForm({ ...form, payment_method: e.target.value })} data-testid="exp-payment" className={fieldCls}>{PAYMENT_METHODS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></EF>
          <EF label="PAYMENT STATUS"><select value={form.payment_status} onChange={(e) => setForm({ ...form, payment_status: e.target.value })} data-testid="exp-status" className={fieldCls}>{PAYMENT_STATUSES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></EF>
          {form.payment_status === "partial" && <EF label="AMOUNT PAID £"><input type="number" step="0.01" min="0" value={form.paid_amount} onChange={(e) => setForm({ ...form, paid_amount: e.target.value })} data-testid="exp-paid-amount" className={fieldCls} /></EF>}

          <div className="sm:col-span-2"><EF label="NOTES"><textarea value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="exp-notes" rows={2} className={fieldCls} /></EF></div>
          <div className="sm:col-span-2">
            <span className="mb-1.5 block text-[11px] font-bold tracking-widest text-muted-foreground">RECEIPT / SUPPLIER INVOICE</span>
            <div className="flex items-center gap-3">
              <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-card px-3 py-2 text-sm font-semibold text-midnight hover:bg-secondary" data-testid="exp-receipt-label">
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />}Attach
                <input type="file" accept="image/*" onChange={onFile} data-testid="exp-receipt-input" className="hidden" />
              </label>
              {form.receipt_url && <img src={absUrl(form.receipt_url)} alt="receipt" className="h-12 w-12 rounded object-cover" />}
            </div>
          </div>
        </div>
        <div className="mt-2 flex items-center justify-between border-t border-border pt-3">
          <span className="text-sm font-bold text-midnight">Total {money(total)}</span>
          <div className="flex gap-2">
            <button onClick={onClose} className="rounded-lg border border-border px-4 py-2 text-sm font-semibold">Cancel</button>
            <button onClick={save} disabled={saving || uploading} data-testid="exp-save-btn" className="inline-flex items-center gap-2 rounded-lg bg-brand px-4 py-2 text-sm font-bold text-white disabled:opacity-60">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}Save</button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
    <PartPicker open={pickerOpen} onClose={() => setPickerOpen(false)} onPick={(p) => setPp((prev) => ({ ...prev, part: p, newPartName: "", unit_cost: p.cost_price != null ? p.cost_price : prev.unit_cost }))} />
    </>
  );
}

const fieldCls = "w-full rounded-lg border border-input px-3 py-2.5 text-sm outline-none focus:border-brand";

function EF({ label, children }) {
  return <label className="block"><span className="mb-1.5 block text-[11px] font-bold tracking-widest text-muted-foreground">{label}</span>{children}</label>;
}

const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];

function AccountingExport() {
  const nowD = new Date();
  const [month, setMonth] = useState(nowD.getMonth() + 1);
  const [year, setYear] = useState(nowD.getFullYear());
  const [busy, setBusy] = useState("");
  const years = Array.from({ length: 6 }, (_, i) => nowD.getFullYear() - i);

  const dl = async (fmt) => {
    setBusy(fmt);
    try {
      await downloadPdf(`/reports/accounting?month=${month}&year=${year}&format=${fmt}`, `accounting-${year}-${String(month).padStart(2,"0")}.${fmt}`);
      toast.success(`${fmt.toUpperCase()} downloaded`);
    } catch (e) { toast.error(e.message || "Export failed"); } finally { setBusy(""); }
  };

  return (
    <div className="mb-6 rounded-xl border border-border bg-card p-5" data-testid="accounting-export">
      <div className="mb-3 flex items-center gap-2"><FileSpreadsheet className="h-5 w-5 text-brand" /><h3 className="font-display text-lg font-bold uppercase text-midnight">Monthly Accounting Export</h3></div>
      <p className="mb-3 text-sm text-muted-foreground">Money IN vs OUT for your accountant — with VAT and monthly totals.</p>
      <div className="flex flex-wrap items-end gap-3">
        <label className="block"><span className="mb-1.5 block text-[11px] font-bold tracking-widest text-muted-foreground">MONTH</span>
          <select value={month} onChange={(e) => setMonth(Number(e.target.value))} data-testid="acc-month" className="rounded-lg border border-input px-3 py-2.5 text-sm outline-none focus:border-brand">
            {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}</select></label>
        <label className="block"><span className="mb-1.5 block text-[11px] font-bold tracking-widest text-muted-foreground">YEAR</span>
          <select value={year} onChange={(e) => setYear(Number(e.target.value))} data-testid="acc-year" className="rounded-lg border border-input px-3 py-2.5 text-sm outline-none focus:border-brand">
            {years.map((y) => <option key={y} value={y}>{y}</option>)}</select></label>
        <button onClick={() => dl("pdf")} disabled={!!busy} data-testid="acc-pdf-btn" className="inline-flex items-center gap-2 rounded-lg bg-brand px-4 py-2.5 text-sm font-bold text-white disabled:opacity-60">{busy === "pdf" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}PDF</button>
        <button onClick={() => dl("csv")} disabled={!!busy} data-testid="acc-csv-btn" className="inline-flex items-center gap-2 rounded-lg border border-brand px-4 py-2.5 text-sm font-bold text-brand disabled:opacity-60">{busy === "csv" ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4" />}CSV / Excel</button>
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, tone, onClick, testid }) {
  const tones = { brand: "text-brand bg-brand-tertiary", success: "text-success bg-success/10", warning: "text-warning bg-warning-bg", midnight: "text-midnight bg-secondary" };
  const inner = (
    <>
      <div className={`mb-3 flex h-10 w-10 items-center justify-center rounded-lg ${tones[tone]}`}><Icon className="h-5 w-5" /></div>
      <p className="font-display text-2xl font-bold text-midnight">{value}</p>
      <p className="mt-0.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</p>
    </>
  );
  if (onClick) {
    return <button onClick={onClick} data-testid={testid} className="rounded-xl border border-border bg-card p-5 text-left transition-colors hover:border-brand hover:bg-secondary">{inner}</button>;
  }
  return <div className="rounded-xl border border-border bg-card p-5">{inner}</div>;
}
